import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import useDataContext from 'data/context/data';

import { money } from '../../lib/landingPrice';
import { ProductDetailLink } from '../pageLinks';
import PdpIcon from '../product/pdpIcon';
import { Picture, PriceTag, SectionHead, imageShape } from '../landing/shared';

const BUNDLE_ID = 'bundle-engineer-print-calendar';

// Phones compare the three formats in a table before the stacked cards. Desktop shows the cards
// side by side, so the table is phone only.
const GlanceTable = ({ calendars }) => (
  <table className="cal-glance lnd-phone">
    <thead>
      <tr>
        <td />
        {calendars.map(({ productId, short }) => (
          <th key={productId} scope="col">
            {short}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {[
        ['Size', ({ size }) => size],
        ['Photos', ({ glance }) => glance.photos],
        ['Starts', ({ glance }) => glance.starts],
        ['Perforated', ({ glance }) => glance.perforated ?? '–'],
      ].map(([label, value]) => (
        <tr key={label}>
          <th scope="row">{label}</th>
          {calendars.map((calendar) => (
            <td key={calendar.productId}>{value(calendar)}</td>
          ))}
        </tr>
      ))}
      <tr>
        <th scope="row">Price</th>
        {calendars.map(({ productId, price }) => (
          <td key={productId}>
            <PriceTag price={price} className="cal-glance-price" />
          </td>
        ))}
      </tr>
    </tbody>
  </table>
);

// Format chooser: every card lists the same lines in the same order. The CTA is the only link,
// so the card body stays plain text.
const CalendarCards = ({ id, calendars, track }) => {
  const dataContext = useDataContext();
  const bundle = dataContext.getSkuProduct(BUNDLE_ID);

  return (
    <section id={id} className="lnd-section cal-compare">
      <Container>
        <SectionHead
          eyebrow="Compare formats"
          title="Choose your calendar"
          aside={
            <span className="lnd-desk">
              Every calendar is printed with your own photos. The cards follow the same order, so
              you can compare them line by line.
            </span>
          }
        />
        <GlanceTable calendars={calendars} />
        <ul className="cal-cards">
          {calendars.map((calendar) => (
            <li key={calendar.productId} className="cal-card">
              <span className="cal-card-photo">
                <Picture
                  image={calendar.image}
                  sizes="(min-width: 992px) 384px, (min-width: 576px) 50vw, 100vw"
                />
              </span>
              <div className="cal-card-body">
                <div>
                  <h3 className="cal-card-name">{calendar.name}</h3>
                  <p className="cal-card-size">
                    <span className="cal-card-size-value">{calendar.size}</span>
                    <span className="cal-card-size-detail">{calendar.sizeDetail}</span>
                  </p>
                </div>
                <dl className="cal-specs">
                  {calendar.specs.map((spec) => (
                    <div key={spec.label} className="cal-spec">
                      <dt>
                        <PdpIcon name={spec.icon} size={16} />
                        {spec.label}
                      </dt>
                      <dd>{spec.value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="cal-card-highlight">
                  <PdpIcon name={calendar.highlight.icon} size={18} />
                  {calendar.highlight.text}
                </p>
                <p className="cal-card-price">
                  {calendar.price.priceFrom && (
                    <span className="cal-card-price-prefix">Starting at</span>
                  )}
                  <PriceTag price={{ ...calendar.price, priceFrom: false }} />
                </p>
                <ProductDetailLink productId={calendar.productId}>
                  <a
                    className="lnd-btn cal-card-cta"
                    onClick={track('product', 'compare', calendar.productId)}
                  >
                    View {calendar.name}
                    <PdpIcon name="arrowRight" size={16} />
                  </a>
                </ProductDetailLink>
              </div>
            </li>
          ))}
        </ul>
        <p className="cal-footnote">
          <PdpIcon name="info" size={16} className="lnd-desk" />
          <span>
            Prices are per calendar before shipping.
            {bundle &&
              ` Engineer Print Calendar & Rails is also available as a set for ${money(bundle.price, bundle.currency)}.`}
          </span>
        </p>
      </Container>
    </section>
  );
};

const calendarShape = PropTypes.shape({
  productId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  short: PropTypes.string.isRequired,
  size: PropTypes.string.isRequired,
  sizeDetail: PropTypes.string.isRequired,
  specs: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  highlight: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  glance: PropTypes.shape({
    photos: PropTypes.string.isRequired,
    starts: PropTypes.string.isRequired,
    perforated: PropTypes.string,
  }).isRequired,
  image: imageShape.isRequired,
  price: PropTypes.shape({
    price: PropTypes.string,
    originalPrice: PropTypes.string,
    priceFrom: PropTypes.bool,
  }).isRequired,
});

GlanceTable.propTypes = {
  calendars: PropTypes.arrayOf(calendarShape).isRequired,
};

CalendarCards.propTypes = {
  id: PropTypes.string.isRequired,
  calendars: PropTypes.arrayOf(calendarShape).isRequired,
  track: PropTypes.func.isRequired,
};

export default CalendarCards;
